import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, CircularProgress, Alert } from '@mui/material';
import { colors, cardSx } from '../theme/designTheme';
import { useCases } from '../hooks/useCases';

/** Colour configuration per priority */
const priorityCfg = {
  Critical: { color: colors.critical, bg: colors.criticalBg, border: colors.criticalBorder },
  High:     { color: colors.high,     bg: colors.highBg,     border: colors.highBorder     },
  Medium:   { color: colors.medium,   bg: colors.mediumBg,   border: colors.mediumBorder   },
  Low:      { color: colors.low,      bg: colors.lowBg,      border: colors.lowBorder      },
};

const PriorityBadge = ({ level }) => {
  const cfg = priorityCfg[level] || priorityCfg.Medium;
  return (
    <Box sx={{ display: 'inline-flex', px: 1, py: 0.3, borderRadius: '4px', bgcolor: cfg.bg, border: `1px solid ${cfg.border}` }}>
      <Typography sx={{ color: cfg.color, fontFamily: 'Lexend', fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em' }}>
        {(level || 'UNKNOWN').toUpperCase()}
      </Typography>
    </Box>
  );
};

/** Settlement countdown chip — red ≤3d, amber ≤10d, muted otherwise */
const SettlementCountdown = ({ days, date }) => {
  if (days == null || date == null) {
    return <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '11px' }}>—</Typography>;
  }
  const critical = days <= 3;
  const urgent = days <= 10;
  const bg = critical ? colors.criticalBg : urgent ? colors.highBg : colors.surfaceElevated;
  const fg = critical ? colors.critical : urgent ? colors.high : colors.textSecondary;
  const border = critical ? colors.criticalBorder : urgent ? colors.highBorder : colors.border;
  return (
    <Box sx={{
      display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
      px: 1.25, py: 0.5, borderRadius: '6px', bgcolor: bg, border: `1px solid ${border}`, minWidth: 70,
    }}>
      <Typography sx={{ color: fg, fontFamily: 'Lexend', fontSize: '14px', fontWeight: 800, lineHeight: 1 }}>
        {days >= 0 ? `${days}d` : 'OVERDUE'}
      </Typography>
      <Typography sx={{ color: fg, opacity: 0.75, fontFamily: 'Lexend', fontSize: '9px', fontWeight: 600, letterSpacing: '0.08em', mt: 0.25 }}>
        {days >= 0 ? 'TO DECIDE' : date}
      </Typography>
    </Box>
  );
};

/** Tiny badge per alert source */
const AlertSourceBadge = ({ source }) => {
  const isModel = source.type === 'model';
  const fg = isModel ? colors.accent : colors.medium;
  const bg = isModel ? colors.accentDim : colors.mediumBg;
  const border = isModel ? colors.border : colors.mediumBorder;
  return (
    <Chip
      size="small"
      label={`${isModel ? '⚙' : '👤'} ${source.name}`}
      sx={{
        bgcolor: bg, color: fg, border: `1px solid ${border}`, borderRadius: '4px',
        fontFamily: 'Lexend', fontSize: '10px', fontWeight: 600, height: 22, mr: 0.5, mb: 0.5,
      }}
    />
  );
};

const CaseQueue = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useCases({ sort: 'urgency', limit: 100 });

  const cases = data?.cases ?? [];

  const criticalCount = useMemo(
    () => cases.filter((c) => (c.priority || c.risk_level) === 'Critical').length,
    [cases]
  );
  const settlementSoon = useMemo(
    () => cases.filter((c) => c.days_to_settlement != null && c.days_to_settlement <= 10).length,
    [cases]
  );

  return (
    <Box sx={{
      bgcolor: colors.bg, minHeight: '100vh',
      px: { xs: 3, md: 4 }, pt: 3, pb: 5,
    }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ color: colors.accent, fontFamily: 'Lexend', fontSize: '11px', fontWeight: 700, letterSpacing: '0.16em', mb: 0.75 }}>
            INVESTIGATION QUEUE
          </Typography>
          <Typography sx={{ color: colors.textPrimary, fontFamily: 'Lexend', fontWeight: 800, fontSize: '26px', letterSpacing: '-0.01em', textTransform: 'uppercase', lineHeight: 1 }}>
            Case Queue
          </Typography>
          <Typography sx={{ color: colors.textSecondary, fontFamily: 'Lexend', fontSize: '12px', mt: 0.75 }}>
            {isLoading ? 'Loading…' : `${cases.length} cases · ${criticalCount} critical · ${settlementSoon} with deadline ≤10 days`}
          </Typography>
        </Box>
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 2, fontFamily: 'Lexend' }}>
          {error?.message || 'Failed to load cases'}
        </Alert>
      )}

      {/* Column headers */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '90px 90px 1.4fr 1.2fr 2fr 110px 110px',
        gap: 2, px: 2.5, py: 1.25,
      }}>
        {['SCORE', 'CASE ID', 'ENTITY', 'FACILITY', 'ALERT SOURCES', 'PRIORITY', 'DEADLINE'].map((h) => (
          <Typography key={h} sx={{
            color: colors.textMuted, fontFamily: 'Lexend', fontSize: '10px',
            fontWeight: 700, letterSpacing: '0.1em',
          }}>
            {h}
          </Typography>
        ))}
      </Box>

      {/* Rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} sx={{ color: colors.accent }} />
          </Box>
        ) : (
          cases.map((c) => {
            const sources = Array.isArray(c.alert_sources) ? c.alert_sources : [];
            const score = c.composite_score ?? Math.round((c.ai_rating ?? 0) * 100);
            const scoreColor =
              score >= 85 ? colors.critical : score >= 65 ? colors.high : score >= 40 ? colors.medium : colors.low;

            return (
              <Box
                key={c.case_number}
                onClick={() => navigate(`/cases/${c.case_number}`)}
                sx={{
                  ...cardSx,
                  display: 'grid',
                  gridTemplateColumns: '90px 90px 1.4fr 1.2fr 2fr 110px 110px',
                  gap: 2, alignItems: 'center', px: 2.5, py: 2,
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, background 0.15s',
                  '&:hover': { borderColor: colors.accent, bgcolor: colors.surfaceHover },
                }}
              >
                {/* Composite score */}
                <Box sx={{ textAlign: 'left' }}>
                  <Typography sx={{ color: scoreColor, fontFamily: 'Lexend', fontWeight: 800, fontSize: '22px', lineHeight: 1 }}>
                    {score}
                  </Typography>
                  <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '9px', letterSpacing: '0.08em', mt: 0.25 }}>
                    /100
                  </Typography>
                </Box>

                {/* Case ID */}
                <Typography sx={{ color: colors.accent, fontFamily: 'Lexend', fontWeight: 700, fontSize: '14px' }}>
                  REF-{c.case_number}
                </Typography>

                {/* Entity */}
                <Box>
                  <Typography sx={{ color: colors.textPrimary, fontFamily: 'Lexend', fontWeight: 600, fontSize: '13px', lineHeight: 1.2 }}>
                    {c.customer_name || c.customer_id}
                  </Typography>
                  <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '10px', mt: 0.3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {c.assigned_to ? `→ ${c.assigned_to}` : c.status}
                  </Typography>
                </Box>

                {/* Facility */}
                <Box>
                  <Typography sx={{ color: colors.textSecondary, fontFamily: 'Lexend', fontSize: '12px', fontWeight: 500 }}>
                    {c.facility_type || '—'}
                  </Typography>
                  {c.facility_amount != null && (
                    <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '10px', mt: 0.25 }}>
                      ${Number(c.facility_amount).toLocaleString()}
                    </Typography>
                  )}
                </Box>

                {/* Alert sources */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                  {sources.length > 0
                    ? sources.map((s, i) => <AlertSourceBadge key={i} source={s} />)
                    : <Typography sx={{ color: colors.textMuted, fontFamily: 'Lexend', fontSize: '11px' }}>—</Typography>}
                </Box>

                {/* Priority */}
                <PriorityBadge level={c.priority || c.risk_level} />

                {/* Settlement countdown */}
                <SettlementCountdown days={c.days_to_settlement} date={c.settlement_date} />
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default CaseQueue;
